/**
 * The fixed, versioned permission catalog the platform owns. A tenant composes
 * roles out of these; it never invents a permission (a permission exists only
 * because the API enforces it).
 *
 * Classified by suite product so the screen mirrors how the sidebar already
 * gates modules by subscription. `risk: true` marks a sensitive permission that
 * must never be granted by inheritance — export, credentials, publishing,
 * destructive deletes, and team management.
 *
 * Derived from docs/superpowers/specs/2026-08-25-rbac-custom-roles-design.md.
 * `labelKey` / `descKey` resolve against the ROLES.PERM.* i18n bucket.
 */

export interface PermissionDef {
  readonly descKey: string;
  readonly id: string;
  readonly labelKey: string;
  readonly risk?: boolean;
}

export interface PermissionGroup {
  readonly caps: readonly PermissionDef[];
  readonly key: string;
  readonly labelKey: string;
}

export interface ProductDef {
  /** Products flagged `always` are in every plan and cannot be unsubscribed. */
  readonly always?: boolean;
  readonly groups: readonly PermissionGroup[];
  readonly icon: string;
  readonly id: string;
  readonly labelKey: string;
  readonly noteKey: string;
}

export const PERMISSION_CATALOG: readonly ProductDef[] = [
  {
    always: true,
    icon: "fa-shield-halved",
    id: "4comply",
    labelKey: "ROLES.PROD.COMPLY",
    noteKey: "ROLES.PROD.COMPLY_NOTE",
    groups: [
      {
        key: "consent",
        labelKey: "ROLES.GRP.CONSENT",
        caps: [
          { id: "consent.read", labelKey: "ROLES.PERM.CONSENT_READ", descKey: "ROLES.PERM.CONSENT_READ_DESC" },
          { id: "consent.write", labelKey: "ROLES.PERM.CONSENT_WRITE", descKey: "ROLES.PERM.CONSENT_WRITE_DESC" },
          { id: "consent.delete", labelKey: "ROLES.PERM.CONSENT_DELETE", descKey: "ROLES.PERM.CONSENT_DELETE_DESC", risk: true },
        ],
      },
      {
        key: "rights",
        labelKey: "ROLES.GRP.RIGHTS",
        caps: [
          { id: "rights.read", labelKey: "ROLES.PERM.RIGHTS_READ", descKey: "ROLES.PERM.RIGHTS_READ_DESC" },
          { id: "rights.write", labelKey: "ROLES.PERM.RIGHTS_WRITE", descKey: "ROLES.PERM.RIGHTS_WRITE_DESC" },
          { id: "rights.fulfil", labelKey: "ROLES.PERM.RIGHTS_FULFIL", descKey: "ROLES.PERM.RIGHTS_FULFIL_DESC" },
        ],
      },
      {
        key: "config",
        labelKey: "ROLES.GRP.CONFIG",
        caps: [
          { id: "config.read", labelKey: "ROLES.PERM.CONFIG_READ", descKey: "ROLES.PERM.CONFIG_READ_DESC" },
          { id: "config.write", labelKey: "ROLES.PERM.CONFIG_WRITE", descKey: "ROLES.PERM.CONFIG_WRITE_DESC" },
          { id: "config.units.delete", labelKey: "ROLES.PERM.UNITS_DELETE", descKey: "ROLES.PERM.UNITS_DELETE_DESC", risk: true },
        ],
      },
      {
        key: "team",
        labelKey: "ROLES.GRP.TEAM",
        caps: [
          { id: "users.read", labelKey: "ROLES.PERM.USERS_READ", descKey: "ROLES.PERM.USERS_READ_DESC" },
          { id: "users.write", labelKey: "ROLES.PERM.USERS_WRITE", descKey: "ROLES.PERM.USERS_WRITE_DESC", risk: true },
          { id: "billing.read", labelKey: "ROLES.PERM.BILLING_READ", descKey: "ROLES.PERM.BILLING_READ_DESC" },
          { id: "audit.read", labelKey: "ROLES.PERM.AUDIT_READ", descKey: "ROLES.PERM.AUDIT_READ_DESC" },
        ],
      },
      {
        key: "data",
        labelKey: "ROLES.GRP.DATA",
        caps: [
          { id: "data.export", labelKey: "ROLES.PERM.DATA_EXPORT", descKey: "ROLES.PERM.DATA_EXPORT_DESC", risk: true },
          { id: "ai.use", labelKey: "ROLES.PERM.AI_USE", descKey: "ROLES.PERM.AI_USE_DESC" },
        ],
      },
      {
        key: "notifications",
        labelKey: "ROLES.GRP.NOTIFICATIONS",
        caps: [
          { id: "notifications.read", labelKey: "ROLES.PERM.NOTIFICATIONS_READ", descKey: "ROLES.PERM.NOTIFICATIONS_READ_DESC" },
          { id: "notifications.manage", labelKey: "ROLES.PERM.NOTIFICATIONS_MANAGE", descKey: "ROLES.PERM.NOTIFICATIONS_MANAGE_DESC" },
        ],
      },
    ],
  },
  {
    icon: "fa-sliders",
    id: "4preferences",
    labelKey: "ROLES.PROD.PREFERENCES",
    noteKey: "ROLES.PROD.PREFERENCES_NOTE",
    groups: [
      {
        key: "reports",
        labelKey: "ROLES.GRP.PREF_REPORTS",
        caps: [
          { id: "preferences.reports.read", labelKey: "ROLES.PERM.PREF_REPORTS_READ", descKey: "ROLES.PERM.PREF_REPORTS_READ_DESC" },
        ],
      },
      {
        key: "preferences",
        labelKey: "ROLES.GRP.PREFERENCES",
        caps: [
          { id: "preferences.read", labelKey: "ROLES.PERM.PREF_READ", descKey: "ROLES.PERM.PREF_READ_DESC" },
          { id: "preferences.pages.write", labelKey: "ROLES.PERM.PREF_PAGES_WRITE", descKey: "ROLES.PERM.PREF_PAGES_WRITE_DESC" },
          { id: "preferences.catalog.write", labelKey: "ROLES.PERM.PREF_CATALOG_WRITE", descKey: "ROLES.PERM.PREF_CATALOG_WRITE_DESC" },
          { id: "preferences.translations.write", labelKey: "ROLES.PERM.PREF_TRANSLATIONS_WRITE", descKey: "ROLES.PERM.PREF_TRANSLATIONS_WRITE_DESC" },
          { id: "preferences.webhooks.write", labelKey: "ROLES.PERM.PREF_WEBHOOKS_WRITE", descKey: "ROLES.PERM.PREF_WEBHOOKS_WRITE_DESC" },
        ],
      },
      {
        key: "publishing",
        labelKey: "ROLES.GRP.PUBLISHING",
        caps: [
          { id: "preferences.publish", labelKey: "ROLES.PERM.PREF_PUBLISH", descKey: "ROLES.PERM.PREF_PUBLISH_DESC", risk: true },
        ],
      },
    ],
  },
  {
    icon: "fa-diagram-project",
    id: "4bridge",
    labelKey: "ROLES.PROD.BRIDGE",
    noteKey: "ROLES.PROD.BRIDGE_NOTE",
    groups: [
      {
        key: "integrations",
        labelKey: "ROLES.GRP.INTEGRATIONS",
        caps: [
          { id: "bridge.read", labelKey: "ROLES.PERM.BRIDGE_READ", descKey: "ROLES.PERM.BRIDGE_READ_DESC" },
          { id: "bridge.write", labelKey: "ROLES.PERM.BRIDGE_WRITE", descKey: "ROLES.PERM.BRIDGE_WRITE_DESC" },
        ],
      },
      {
        key: "credentials",
        labelKey: "ROLES.GRP.CREDENTIALS",
        caps: [
          { id: "bridge.secrets.write", labelKey: "ROLES.PERM.BRIDGE_SECRETS", descKey: "ROLES.PERM.BRIDGE_SECRETS_DESC", risk: true },
        ],
      },
    ],
  },
  {
    icon: "fa-chart-pie",
    id: "4segments",
    labelKey: "ROLES.PROD.SEGMENTS",
    noteKey: "ROLES.PROD.SEGMENTS_NOTE",
    groups: [
      {
        key: "segments",
        labelKey: "ROLES.GRP.SEGMENTS",
        caps: [
          { id: "segments.read", labelKey: "ROLES.PERM.SEG_ACCESS", descKey: "ROLES.PERM.SEG_ACCESS_DESC" },
        ],
      },
    ],
  },
];

/** All permission ids in the catalog, flattened. */
export const ALL_PERMISSION_IDS: readonly string[] = PERMISSION_CATALOG
  .flatMap((p) => p.groups)
  .flatMap((g) => g.caps)
  .map((c) => c.id);

/** Lookup a permission definition by id (undefined if not in the catalog). */
export const PERMISSION_BY_ID: ReadonlyMap<string, PermissionDef> = new Map(
  PERMISSION_CATALOG
    .flatMap((p) => p.groups)
    .flatMap((g) => g.caps)
    .map((c) => [c.id, c])
);

/**
 * The four system roles seeded into every tenant. Used as the screen's default
 * until the backend serves ClientRole documents. `key` matches the existing
 * `user_level` values so no per-user migration is needed.
 */
export const SYSTEM_ROLE_SEEDS: ReadonlyArray<{
  color: number;
  descKey: string;
  key: string;
  nameKey: string;
  permissions: readonly string[];
}> = [
  {
    key: "admin",
    nameKey: "ROLES.SYS.ADMIN",
    descKey: "ROLES.SYS.ADMIN_DESC",
    color: 0,
    permissions: ALL_PERMISSION_IDS,
  },
  {
    key: "config",
    nameKey: "ROLES.SYS.CONFIG",
    descKey: "ROLES.SYS.CONFIG_DESC",
    color: 1,
    permissions: [
      "config.read", "config.write",
      "preferences.read", "preferences.reports.read", "preferences.pages.write",
      "preferences.catalog.write", "preferences.translations.write",
      "preferences.webhooks.write", "preferences.publish",
      "consent.read", "rights.read", "bridge.read", "segments.read", "ai.use",
      "notifications.read", "notifications.manage",
    ],
  },
  {
    key: "legal",
    nameKey: "ROLES.SYS.LEGAL",
    descKey: "ROLES.SYS.LEGAL_DESC",
    color: 2,
    permissions: [
      "rights.read", "rights.write", "rights.fulfil",
      "consent.read", "consent.delete", "audit.read", "data.export",
      "notifications.read",
    ],
  },
  {
    key: "basic",
    nameKey: "ROLES.SYS.BASIC",
    descKey: "ROLES.SYS.BASIC_DESC",
    color: 3,
    permissions: [
      "consent.read", "rights.read", "config.read",
      "preferences.read", "preferences.reports.read", "bridge.read", "segments.read",
      "notifications.read",
    ],
  },
];

/**
 * Example custom roles shown only in the no-backend preview mode, so the screen
 * demonstrates the delete and colour controls (which are available on custom
 * roles, never the locked system ones) exactly as the design mockup does. Once
 * the backend is wired, the tenant's real custom roles replace these.
 */
export const CUSTOM_ROLE_PREVIEW_SEEDS: ReadonlyArray<{
  color: number;
  description: string;
  key: string;
  name: string;
  permissions: readonly string[];
}> = [
  {
    key: "privacy-analyst",
    name: "Privacy Analyst",
    description: "Handles rights requests, reads consent, can export",
    color: 4,
    permissions: ["rights.read", "rights.write", "rights.fulfil", "consent.read", "audit.read", "data.export"],
  },
  {
    key: "marketing-ops",
    name: "Marketing Ops",
    description: "Builds and publishes preference pages",
    color: 5,
    permissions: ["preferences.read", "preferences.pages.write", "preferences.publish", "bridge.read", "segments.read"],
  },
];

/** Distinct role tints, indexed by ClientRole.color. */
export const ROLE_PALETTE: readonly string[] = [
  "#b32024", // admin — brand red
  "#1f6feb", // config — accent blue
  "#7c4dff", // legal — violet
  "#6b7a8c", // basic — muted
  "#0f9d58", // custom — green
  "#d9822b", // custom — amber
  "#c2185b", // custom — magenta
  "#00838f", // custom — teal
];

/**
 * i18n label/description keys for the four system roles, keyed by role key.
 *
 * Separate from SYSTEM_ROLE_SEEDS above, which uses the ROLES.SYS.* bucket for the roles
 * *editor*. These are the USER.*_LEVEL keys used wherever a role is shown as an attribute of a
 * person — the users grid and the header user menu — so both surfaces name a role identically.
 *
 * A custom role is not here: it renders its own `name`, which passes through the `translate`
 * pipe unchanged when no key matches it.
 */
export const SYSTEM_ROLE_LABELS: Readonly<Record<string, { descKey: string; label: string }>> = {
  admin: { descKey: "USER.ADMIN_LEVEL_DESC", label: "USER.ADMIN_LEVEL" },
  basic: { descKey: "USER.BASIC_LEVEL_DESC", label: "USER.BASIC_LEVEL" },
  config: { descKey: "USER.CONFIG_LEVEL_DESC", label: "USER.CONFIG_LEVEL" },
  legal: { descKey: "USER.LEGAL_LEVEL_DESC", label: "USER.LEGAL_LEVEL" },
};
